import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import Dropdown from 'react-bootstrap/Dropdown';
import moment from 'moment';

import Avatar from '../../../shared/_TBComponents/Avatar/Avatar';
import Text from '../../../shared/_TBComponents/Text';
import iconSet from '../../../shared/images/teambuildr-selection.json';

import { getImageSource } from '../utils';
import {
  ColorGray,
  ColorGreen,
  ColorPrimary,
  ColorWhite,
} from '../../../shared/GlobalStyles';

import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import {
  deleteJournalEntryComment,
  openImageLightbox,
  openMessageVideo,
  setActiveReduxModal,
  setEditComment,
  setReportPostId,
} from '../ducks/journalActions';

const DROPDOWN_TEXT_PROPS = {
  fontSize: 14,
  marginLeft: '10px',
  paddingTop: '2px',
  fontWeight: 900,
  textTransform: 'uppercase',
  letterSpacing: 1.3,
  color: ColorGray,
};

const MEDIA_BOX_STYLES = `
  align-items: center;
  display: flex;
  height: 100px;
  justify-content: center;
  min-height: 100px;
  min-width: 100px;
  position: relative;
  width: 100px;
`;

const AvatarContainer = styled('div')`
  align-items: center;
  display: flex;
  height: 32px;
  justify-content: center;
  margin-right: 12px;
  width: 32px;
`;

const CommentAuthor = styled('div')`
  color: ${ColorPrimary};
  font-size: 14px;
  font-weight: bold;
`;

const CommentHeaderDiv = styled('div')`
  align-items: top;
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const CommentInfo = styled('div')`
  align-items: center;
  display: flex;
  width: 80%;
`;

const CommentMediaContainer = styled('div')`
  padding: 10px 20px 0 44px;
`;

const CommentText = styled('div')`
  color: ${ColorPrimary};
  font-size: 16px;
  line-height: 20px;
  padding-left: 44px;
`;

const CommentTimestamp = styled('div')`
  color: ${ColorGray};
  font-size: 12px;
`;

const JournalComment = styled('div')`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const MediaBox = styled('button')`
  ${MEDIA_BOX_STYLES}
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const MediaBoxDiv = styled('div')`
  ${MEDIA_BOX_STYLES}
`;

const MediaImage = styled('img')`
  border-radius: 10px;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const NameDateDiv = styled('div')`
  line-height: 16px;
`;

const OptionsDropdownWrapper = styled('div')`
  .btn-primary {
    background-color: transparent;
    border: none;
    height: 20px;
    padding: 0;
    &hover {
      background-color: transparent;
      border: none;
    }
  }
  .cancel-edit {
    align-items: top;
    cursor: pointer;
    display: flex;
    height: 32px;
    text-align: center;
  }
  .show > .btn-primary.dropdown-toggle {
    background-color: transparent;
    border: none;
    color: ${ColorPrimary};
  }
  .dropdown-menu {
    padding: 0px;
  }
  .dropdown-item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 5px;
    padding-left: 10px;
    transition: none;
    text-transform: uppercase;
    svg {
      fill: ${ColorGray};
    }
    :hover {
      transition: linear 0s;
      background: ${ColorGreen};
      color: ${ColorWhite};
      div p {
        color: ${ColorWhite};
      }
      svg {
        fill: ${ColorWhite};
      }
    }
  }
`;

const OptionsIconContainer = styled('div')`
  align-items: top;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

const PlayIconContainer = styled('button')`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.4);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  display: flex;
  height: 60px;
  justify-content: center;
  left: 0;
  margin-left: auto;
  margin-right: auto;
  position: absolute;
  right: 0;
  width: 60px;
  z-index: 1;
`;

const Comment = ({ comment, journalEntry }) => {
  const {
    accountCode,
    id: currentUserId,
    userTimeZone,
    userSettings,
  } = useSelector((state) => state.auth.data.currentUser);
  const editedComment = useSelector(
    (state) => state.journal.data.editedComment,
  );
  const dispatch = useDispatch();

  const {
    commentText,
    createdAt,
    userName,
    userPic,
    userId: commentAuthorId,
  } = comment;

  const currentUserIsAuthor = currentUserId === commentAuthorId;
  const isEditing = editedComment?.comment?.id === comment?.id;
  const commentRef = useRef(null);
  const wasEditingRef = useRef(false);

  // Scroll comment into view when editing ends (cancel or successful edit)
  useEffect(() => {
    if (wasEditingRef.current && !isEditing && commentRef.current) {
      commentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    wasEditingRef.current = isEditing;
  }, [isEditing]);

  const formatDate = (utcDateStr) => {
    const timeZone = userTimeZone || userSettings?.timeZone;
    return moment
      .utc(utcDateStr)
      .tz(timeZone)
      .format('MMMM D, YYYY h:mm A');
  };

  const handleDeleteComment = () => {
    const values = {
      entryId: journalEntry?.id,
      commentId: comment?.id,
    };
    dispatch(deleteJournalEntryComment(accountCode, values));
    journalTracker('Journal entry comment', 'Comment deleted');
  };

  const handleEditComment = () => {
    dispatch(
      setEditComment({
        entryId: journalEntry?.id,
        comment,
      }),
    );
    journalTracker('Journal entry comment', 'Activate edit comment');
  };

  const handleReportComment = () => {
    dispatch(setActiveReduxModal('report-post-modal'));
    dispatch(setReportPostId({ type: 'Comment', id: comment?.id }));
    journalTracker('Journal entry comment', 'Report post modal opened');
  };

  const handleImageClick = () => {
    if (
      comment?.media?.type === 'IMAGE'
      || comment?.media?.type === 'EXTERNAL_URL'
    ) {
      dispatch(openImageLightbox(comment.media.mediaUrl));
      journalTracker('Journal entry comment', 'Image clicked - Image lightbox modal opened');
    }
  };

  const handleVideoClick = () => {
    dispatch(openMessageVideo(comment?.media?.mediaUrl));
    journalTracker('Journal entry comment', 'Video clicked - Play video modal opened');
  };

  const renderCommentInfo = () => (
    <CommentInfo>
      <AvatarContainer>
        <Avatar avatar={userPic || ''} size={32} />
      </AvatarContainer>
      <NameDateDiv>
        <CommentAuthor>{userName}</CommentAuthor>
        <CommentTimestamp>{formatDate(createdAt)}</CommentTimestamp>
      </NameDateDiv>
    </CommentInfo>
  );

  const renderDropdownMenuItems = () => {
    if (currentUserIsAuthor) {
      return (
        <>
          <Dropdown.Item onClick={handleEditComment}>
            <IcomoonReact iconSet={iconSet} size={15} icon='pencil' />
            <Text styledTextProps={DROPDOWN_TEXT_PROPS}>Edit</Text>
          </Dropdown.Item>
          <Dropdown.Item onClick={handleDeleteComment}>
            <IcomoonReact iconSet={iconSet} size={15} icon='trashcan' />
            <Text styledTextProps={DROPDOWN_TEXT_PROPS}>Delete</Text>
          </Dropdown.Item>
        </>
      );
    }

    return (
      <Dropdown.Item onClick={handleReportComment}>
        <IcomoonReact iconSet={iconSet} size={15} icon='warning' />
        <Text styledTextProps={DROPDOWN_TEXT_PROPS}>Report Post</Text>
      </Dropdown.Item>
    );
  };

  const renderOptionsDropdown = () => (
    <OptionsDropdownWrapper>
      {isEditing ? (
        <IcomoonReact
          iconSet={iconSet}
          size={20}
          icon='pencil'
          color={ColorGray}
          aria-label='Comment is being edited'
        />
      ) : (
        <Dropdown alignRight>
          <Dropdown.Toggle
            className='dropdown-toggle'
            aria-label='Comment options'
          >
            <OptionsIconContainer>
              <IcomoonReact
                iconSet={iconSet}
                size={20}
                icon='dots'
                color={ColorGray}
              />
            </OptionsIconContainer>
          </Dropdown.Toggle>
          <Dropdown.Menu>{renderDropdownMenuItems()}</Dropdown.Menu>
        </Dropdown>
      )}
    </OptionsDropdownWrapper>
  );

  const renderCommentHeader = () => (
    <CommentHeaderDiv>
      {renderCommentInfo()}
      {renderOptionsDropdown()}
    </CommentHeaderDiv>
  );

  const renderCommentText = () => {
    if (!commentText) return null;
    return <CommentText>{commentText}</CommentText>;
  };

  const renderCommentMedia = () => {
    if (!comment?.media) return null;

    const isVideo = comment?.media?.type === 'VIDEO';
    const MediaWrapper = isVideo ? MediaBoxDiv : MediaBox;

    return (
      <CommentMediaContainer>
        <MediaWrapper
          onClick={!isVideo ? handleImageClick : undefined}
          aria-label={!isVideo ? 'View comment image' : undefined}
        >
          {comment?.media?.mediaUrl && (
            <MediaImage
              src={getImageSource(comment?.media)}
              alt='Comment media'
            />
          )}
          {isVideo && (
            <PlayIconContainer
              onClick={handleVideoClick}
              aria-label='Play video'
            >
              <IcomoonReact
                iconSet={iconSet}
                size={40}
                icon='play-filled'
                color={ColorWhite}
              />
            </PlayIconContainer>
          )}
        </MediaWrapper>
      </CommentMediaContainer>
    );
  };

  return (
    <JournalComment ref={commentRef} data-comment-id={comment?.id}>
      {renderCommentHeader()}
      {renderCommentText()}
      {renderCommentMedia()}
    </JournalComment>
  );
};

Comment.propTypes = {
  comment: PropTypes.shape({
    commentText: PropTypes.string,
    createdAt: PropTypes.string,
    id: PropTypes.number,
    media: PropTypes.instanceOf(Object),
    userId: PropTypes.number,
    userName: PropTypes.string,
    userPic: PropTypes.string,
  }).isRequired,
  journalEntry: PropTypes.instanceOf(Object).isRequired,
};

export default Comment;
